import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Check, Mail } from "lucide-react";
import { api, imageUrl, formatPrice } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useSettings } from "@/contexts/SettingsContext";
import { toast } from "sonner";

export default function Store() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { items, addItem } = useCart();
  const { payments_enabled } = useSettings();

  useEffect(() => {
    api
      .get("/products")
      .then((res) => setProducts(res.data))
      .finally(() => setLoading(false));
  }, []);

  const inCart = (id) => items.some((i) => i.id === id);

  const handleAdd = (e, p) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(p);
    toast.success("Added to cart", { description: p.title });
  };

  return (
    <div data-testid="store-page" className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
        <div>
          <p className="label-eyebrow">
            <span className="mandala-dot" />The Store
          </p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-[#2D2926] mt-3 tracking-tight">
            Available Works
          </h1>
        </div>
        <p className="text-sm text-[#5B5653] max-w-sm md:text-right">
          Free delivery across Australia. Every piece is one-of-one — once sold it leaves the
          collection.
        </p>
      </div>

      {loading ? (
        <div className="text-center py-20 label-eyebrow-muted">Loading…</div>
      ) : products.length === 0 ? (
        <div
          data-testid="store-empty"
          className="text-center py-20 border border-[#E5D8BD] bg-[#EFEBE6]/40"
        >
          <p className="label-eyebrow mb-3">Coming soon</p>
          <p className="text-[#5B5653] max-w-md mx-auto">
            The next collection is being prepared. Please check back soon.
          </p>
        </div>
      ) : (
        <div
          data-testid="store-grid"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12 lg:gap-16"
        >
          {products.map((p, i) => (
            <article
              key={p.id}
              data-testid={`store-card-${p.id}`}
              className="flex flex-col gap-4 fade-up"
              style={{ animationDelay: `${i * 0.06}s` }}
            >
              <Link to={`/product/${p.id}`} className="block overflow-hidden bg-[#EFEBE6] aspect-[4/5] group">
                <img
                  src={imageUrl(p.image_path)}
                  alt={p.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </Link>
              <div className="flex flex-col gap-1">
                <Link
                  to={`/product/${p.id}`}
                  className="font-serif text-xl text-[#2D2926] tracking-tight hover:text-[#E25C46] transition-colors"
                >
                  {p.title}
                </Link>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#5B5653]">{p.size}</span>
                  <span className="text-[#2D2926] font-medium">{formatPrice(p.price)}</span>
                </div>
              </div>
              {!payments_enabled ? (
                <a
                  href={`mailto:colleen.prosser@outlook.com?subject=${encodeURIComponent(
                    `Enquiry — ${p.title}`
                  )}&body=${encodeURIComponent(
                    `Hi Colleen,\n\nI'd like to enquire about purchasing "${p.title}" (${p.size}, ${formatPrice(p.price)}).\n\nThanks,\n`
                  )}`}
                  data-testid={`enquire-${p.id}`}
                  className="btn-primary !py-3 text-xs"
                >
                  <Mail className="w-4 h-4 mr-2" /> Enquire to Purchase
                </a>
              ) : inCart(p.id) ? (
                <Link
                  to="/checkout"
                  data-testid={`go-checkout-${p.id}`}
                  className="btn-secondary !py-3 text-xs"
                >
                  <Check className="w-4 h-4 mr-2" />
                  In Cart — Go to Checkout
                </Link>
              ) : (
                <button
                  data-testid={`add-to-cart-${p.id}`}
                  onClick={(e) => handleAdd(e, p)}
                  className="btn-primary !py-3 text-xs"
                >
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Add to Cart
                </button>
              )}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
